import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Smartphone, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    shop_name: "",
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success("Compte créé avec succès");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1563770660941-20978e870e26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxlbGVjdHJvbmljcyUyMHJlcGFpciUyMHRlY2huaWNpYW4lMjBzb2xkZXJpbmclMjBzbWFydHBob25lfGVufDB8fHx8MTc3MDA3NTM4NXww&ixlib=rb-4.1.0&q=85')` 
        }}
      >
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-chivo text-4xl font-bold text-white mb-4">
            Commencez dès aujourd'hui
          </h2>
          <p className="text-white/70 text-lg max-w-md">
            Créez votre compte en quelques secondes et commencez à gérer vos réparations professionnellement.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-primary/20 rounded-md flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-chivo font-bold text-2xl text-foreground">RepairTracker</h1>
              <p className="text-sm text-muted-foreground">Gestion de réparations</p>
            </div>
          </div>

          <h2 className="font-chivo text-3xl font-bold text-foreground mb-2">Créer un compte</h2>
          <p className="text-muted-foreground mb-8">
            Remplissez les informations pour commencer
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Votre nom</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Jean Dupont"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="register-name-input"
                className="bg-secondary/50 border-border focus:border-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shop_name">Nom de votre boutique</Label>
              <Input
                id="shop_name"
                name="shop_name"
                type="text"
                placeholder="Phone Repair Pro"
                value={formData.shop_name}
                onChange={handleChange}
                data-testid="register-shop-input"
                className="bg-secondary/50 border-border focus:border-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="vous@exemple.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="register-email-input"
                className="bg-secondary/50 border-border focus:border-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                data-testid="register-password-input"
                className="bg-secondary/50 border-border focus:border-primary"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-animate"
            >
              {loading ? "Création..." : "Créer mon compte"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-8">
            Déjà un compte ?{" "}
            <Link 
              to="/login" 
              className="text-primary hover:underline"
              data-testid="login-link"
            >
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
